/**
 * Konami code easter egg with particle burst
 */

import { ObjectPool, prefersReducedMotion } from '../core/utils.js';

export class EasterEgg {
  #code = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];
  #position = 0;
  #overlay;
  #closeBtn;
  #particlePool;
  #abortController = new AbortController();

  constructor() {
    this.#overlay = document.getElementById('easterEgg');
    this.#closeBtn = document.getElementById('eeClose');

    if (!this.#overlay) return;

    this.#init();
  }

  #init() {
    const signal = this.#abortController.signal;

    // Konami code listener
    document.addEventListener('keydown', (e) => {
      if (e.code === this.#code[this.#position]) {
        this.#position++;
        if (this.#position === this.#code.length) {
          this.#trigger();
          this.#position = 0;
        }
      } else {
        this.#position = 0;
      }
    }, { signal });

    // Close button
    this.#closeBtn?.addEventListener('click', () => {
      this.#hide();
    }, { signal });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#overlay.classList.contains('active')) {
        this.#hide();
      }
    }, { signal });

    // Initialize particle pool
    if (!prefersReducedMotion()) {
      this.#initParticlePool();
    }
  }

  #initParticlePool() {
    this.#particlePool = new ObjectPool(
      () => {
        const el = document.createElement('div');
        el.className = 'particle';
        return el;
      },
      (el) => {
        el.style.cssText = '';
        if (el.parentNode) el.parentNode.removeChild(el);
      },
      30
    );
  }

  #trigger() {
    this.#show();
    
    if (!prefersReducedMotion()) {
      this.#burstParticles();
    }
  }

  #show() {
    this.#overlay.classList.add('active');
    this.#overlay.setAttribute('aria-hidden', 'false');
    this.#overlay.focus();
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  #hide() {
    this.#overlay.classList.remove('active');
    this.#overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    // Release all particles
    this.#particlePool?.releaseAll();
  }

  #burstParticles() {
    const colors = ['#d4af37', '#f5d76e', '#b8860b', '#ffe066', '#c8960c'];
    const count = prefersReducedMotion() ? 0 : 50;

    for (let i = 0; i < count; i++) {
      const particle = this.#particlePool.acquire();
      if (!particle) continue;

      const angle = (Math.PI * 2 / count) * i + Math.random() * 0.5;
      const dist = 100 + Math.random() * 200;

      Object.assign(particle.style, {
        position: 'fixed',
        left: '50%',
        top: '50%',
        width: `${Math.random() * 8 + 3}px`,
        height: `${Math.random() * 8 + 3}px`,
        background: colors[Math.floor(Math.random() * colors.length)],
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: '9999',
        transform: 'translate(-50%, -50%)'
      });

      document.body.appendChild(particle);

      const animation = particle.animate([
        { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
        { transform: `translate(calc(-50% + ${Math.cos(angle) * dist}px), calc(-50% + ${Math.sin(angle) * dist}px)) scale(0)`, opacity: 0 }
      ], {
        duration: 800 + Math.random() * 600,
        easing: 'cubic-bezier(.23,1,.32,1)'
      });

      animation.onfinish = () => {
        this.#particlePool.release(particle);
      };
    }
  }

  destroy() {
    this.#abortController.abort();
    this.#particlePool?.releaseAll();
  }
}
